/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';

import { getBrandName } from '../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getIsBot } from '../../../state/ducks/App/App-Selectors';

import useBrowserUUID from '../useBrowserUUID';
import useGetAccessTokenSafely from '../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../gql';
import findRecentOrderStatus from '../../gql/queries/findRecentOrderStatus';
import { getUserEmailId } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';

/**
 * @description Hook to fetch recent order status for a user
 * @param {object} options - hook options
 * @param {number} options.daysInPast - how many days back to look
 * @param {number} options.daysInFuture - how many days forward to look
 * @param {object} [options.apolloOptions] - overrides | additional options to pass to useQuery API
 * @returns {object} { loading, error, data, variables }
 */

const useGetRecentOrderStatus = ({
    daysInPast = 7,
    daysInFuture = 14,
    apolloOptions = {},
} = {}) => {
    const isBot = useSelector(getIsBot);
    if (isBot) {
        return {
            loading: false, error: false, data: null, variables: { skip: false },
        };
    }

    const query = findRecentOrderStatus();
    const emailId = useSelector(getUserEmailId);
    const brand = useSelector(getBrandName) || '1800flowers';
    const browserUUID = useBrowserUUID();
    const jwt = useGetAccessTokenSafely();

    // skip query until we have a jwt and browser id
    const skip = Boolean(!jwt || !browserUUID);

    return useQuery(query, {
        variables: skip
            ? { skip }
            : {
                brand,
                environment: GRAPHQL_ENV,
                user: {
                    anonymousId: browserUUID,
                    ...(emailId ? { emailId } : {}),
                },
                daysInPast,
                daysInFuture,
            },
        context: { api: 'personalizedContent', jwt },
        fetchPolicy: 'no-cache',
        skip,
        ...apolloOptions,
    });
};

export default useGetRecentOrderStatus;
